'use client';

import { useEffect, useState, useCallback, useMemo, memo, useRef } from 'react';
import { useRouter } from 'next/navigation';
import Link from 'next/link';
import { useAuth } from '@/contexts/AuthContext';
import { tmdb } from '@/lib/tmdb';
import { useStore } from '@/lib/store';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Search,
  TrendingUp,
  Star,
  Film,
  Tv,
  PlayCircle,
  Calendar,
  ChevronRight,
  Sparkles,
  Clock,
  ArrowRight,
  Info,
  Plus
} from 'lucide-react';
import Image from 'next/image';
import { MediaCard as ReusableMediaCard } from '@/components/MediaCard';
import { CinematicHero } from '@/components/cinematic/CinematicHero';
import { PersonalizedRow } from '@/components/PersonalizedRow';
import { DynamicPersonalizedRows } from '@/components/DynamicPersonalizedRows';
import { detectUserLanguages } from '@/lib/language-preferences';
import { Header } from '@/components/Header';
import { getActiveSeasonalPeriod, SeasonalPeriod, shuffleWithSeed, getDailySeed } from '@/lib/seasonal-movies';
import { useTheme } from '@/contexts/ThemeContext';
import { CategoryBrowser } from '@/components/CategoryBrowser';
import { db } from '@/lib/firebase';
import { doc, getDoc } from 'firebase/firestore';

// Landing page section configuration
interface LandingPageSection {
  id: string;
  title: string;
  enabled: boolean;
  order: number;
  requiresAuth: boolean;
  description?: string;
}

// Default section configuration (must match admin page)
const DEFAULT_LANDING_SECTIONS: LandingPageSection[] = [
  // Personalized first — most relevant to the user
  { id: 'continue-watching', title: 'Continue Watching', enabled: true, order: 0, requiresAuth: true },
  { id: 'recommended-for-you', title: 'Recommended For You', enabled: true, order: 1, requiresAuth: true },
  { id: 'dynamic-personalized', title: 'Because You Like / Watched', enabled: true, order: 2, requiresAuth: true },
  { id: 'watch-again', title: 'Watch Again', enabled: true, order: 3, requiresAuth: true },
  { id: 'language-specific', title: 'Popular in Your Language', enabled: true, order: 4, requiresAuth: true },
  { id: 'trending-personalized', title: 'Trending Now (Personalized)', enabled: true, order: 5, requiresAuth: true },
  // Then the catalog (trending / popular / top rated / releases)
  { id: 'trending', title: 'Trending Now', enabled: true, order: 6, requiresAuth: false },
  { id: 'popular-movies', title: 'Popular Movies', enabled: true, order: 7, requiresAuth: false },
  { id: 'popular-tv', title: 'Popular TV Shows', enabled: true, order: 8, requiresAuth: false },
  { id: 'top-rated-movies', title: 'All-Time Top Rated Movies', enabled: true, order: 9, requiresAuth: false },
  { id: 'top-rated-tv', title: 'All-Time Top Rated TV Shows', enabled: true, order: 10, requiresAuth: false },
  { id: 'recently-released', title: 'Recently Released', enabled: true, order: 11, requiresAuth: false },
  { id: 'coming-soon', title: 'Coming Soon', enabled: true, order: 12, requiresAuth: false },
  // Browsing helpers at the bottom
  { id: 'seasonal', title: 'Seasonal Movies', enabled: true, order: 13, requiresAuth: false },
  { id: 'category-browser', title: 'Browse by Genre', enabled: true, order: 14, requiresAuth: false },
];

interface MediaItem {
  id: number;
  title?: string;
  name?: string;
  poster_path: string | null;
  backdrop_path: string | null;
  vote_average: number;
  release_date?: string;
  first_air_date?: string;
  media_type?: string;
  overview?: string;
}

export default function Home() {
  const { user } = useAuth();
  const router = useRouter();
  const { addToWatchlist, watchlist } = useStore();
  const { activeTheme, loading: themeLoading } = useTheme();
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState<MediaItem[]>([]);
  const [trending, setTrending] = useState<MediaItem[]>([]);
  const [popularShows, setPopularShows] = useState<MediaItem[]>([]);
  const [popularMovies, setPopularMovies] = useState<MediaItem[]>([]);
  const [topRatedShows, setTopRatedShows] = useState<MediaItem[]>([]);
  const [topRatedMovies, setTopRatedMovies] = useState<MediaItem[]>([]);
  const [upcoming, setUpcoming] = useState<MediaItem[]>([]);
  const [recentlyReleased, setRecentlyReleased] = useState<MediaItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchLoading, setSearchLoading] = useState(false);
  const [activeTab, setActiveTab] = useState('all');
  const [featuredContent, setFeaturedContent] = useState<MediaItem | null>(null);
  const [page, setPage] = useState(1);
  const [loadingMore, setLoadingMore] = useState(false);
  const [hasMore, setHasMore] = useState(true);
  const [userLanguages, setUserLanguages] = useState<string[]>([]);
  const [seasonalPeriod, setSeasonalPeriod] = useState<SeasonalPeriod | null>(null);
  const [seasonalMovies, setSeasonalMovies] = useState<MediaItem[]>([]);
  const [sectionConfig, setSectionConfig] = useState<LandingPageSection[]>(DEFAULT_LANDING_SECTIONS);

  // Fetch landing page section configuration
  const fetchSectionConfig = async () => {
    try {
      const settingsRef = doc(db, 'settings', 'landingPageSections');
      const settingsDoc = await getDoc(settingsRef);

      if (settingsDoc.exists()) {
        const data = settingsDoc.data();
        if (data.sections && Array.isArray(data.sections)) {
          const savedSections = data.sections as LandingPageSection[];
          // Merge saved sections with defaults
          const mergedSections = DEFAULT_LANDING_SECTIONS.map(defaultSection => {
            const saved = savedSections.find(s => s.id === defaultSection.id);
            return saved ? { ...defaultSection, ...saved } : defaultSection;
          });
          mergedSections.sort((a, b) => a.order - b.order);
          setSectionConfig(mergedSections);
        }
      }
    } catch (error) {
      // Use defaults on error
    }
  };

  // Helper to check if a section should be rendered
  const shouldRenderSection = (sectionId: string) => {
    const section = sectionConfig.find(s => s.id === sectionId);
    if (!section || !section.enabled) return false;
    if (section.requiresAuth && !user) return false;
    return true;
  };

  // TMDB's trending / popular / top-rated lists overlap heavily, which made
  // the catalog rows look identical. Within each tab, a title only appears in
  // the first row that claims it, so every row shows distinct content.
  const dedupedRows = useMemo(() => {
    const makeClaimer = () => {
      const used = new Set<string>();
      return (items: MediaItem[], fallbackType: 'movie' | 'tv') =>
        items
          .filter(item => {
            const key = `${item.media_type || fallbackType}-${item.id}`;
            if (used.has(key)) return false;
            used.add(key);
            return true;
          })
          .slice(0, 20);
    };

    // All tab — claim in default row order; the trending row only renders for
    // logged-out users, so it only claims titles in that case
    const claimAll = makeClaimer();
    const all = {
      trending: user ? trending : claimAll(trending, 'tv'),
      popularMovies: claimAll(popularMovies, 'movie'),
      popularShows: claimAll(popularShows, 'tv'),
      topRatedMovies: claimAll(topRatedMovies, 'movie'),
      topRatedShows: claimAll(topRatedShows, 'tv'),
      recentlyReleased: claimAll(recentlyReleased, 'movie'),
      upcoming: claimAll(upcoming, 'movie'),
    };

    const claimTrendingTab = makeClaimer();
    const trendingTab = {
      trending: claimTrendingTab(trending, 'tv'),
      popularMovies: claimTrendingTab(popularMovies, 'movie'),
      popularShows: claimTrendingTab(popularShows, 'tv'),
      recentlyReleased: claimTrendingTab(recentlyReleased, 'movie'),
    };

    const claimShowsTab = makeClaimer();
    const showsTab = {
      popularShows: claimShowsTab(popularShows, 'tv'),
      topRatedShows: claimShowsTab(topRatedShows, 'tv'),
      trending: claimShowsTab(trending, 'tv'),
    };

    const claimMoviesTab = makeClaimer();
    const moviesTab = {
      popularMovies: claimMoviesTab(popularMovies, 'movie'),
      topRatedMovies: claimMoviesTab(topRatedMovies, 'movie'),
      recentlyReleased: claimMoviesTab(recentlyReleased, 'movie'),
      upcoming: claimMoviesTab(upcoming, 'movie'),
    };

    return { all, trendingTab, showsTab, moviesTab };
  }, [user, trending, popularShows, popularMovies, topRatedShows, topRatedMovies, recentlyReleased, upcoming]);

  useEffect(() => {
    // Fetch section config on mount
    fetchSectionConfig();
  }, []);

  useEffect(() => {
    // Fetch seasonal content first to avoid flickering
    const loadContent = async () => {
      const hasSeasonalContent = await fetchSeasonalContent();
      // Pass whether we have seasonal content to fetchContent
      await fetchContent(hasSeasonalContent);
      if (user) {
        loadUserLanguages();
      }
    };
    loadContent();
  }, [user]);

  const loadUserLanguages = async () => {
    if (!user) return;
    try {
      const languages = await detectUserLanguages(user.uid, watchlist);
      setUserLanguages(languages.primary.concat(languages.secondary));
    } catch (error) {
      setUserLanguages(['en']); // Default to English
    }
  };

  const fetchContent = async (hasSeasonalHero: boolean = false) => {
    try {
      // Two pages of popular/top-rated so rows stay full after cross-row dedup
      const [trendingData, showsData, moviesData, topRatedShowsData, topRatedMoviesData, upcomingData, recentlyReleasedData, showsData2, moviesData2, topRatedShowsData2, topRatedMoviesData2] = await Promise.all([
        tmdb.getTrendingShows('day'),
        tmdb.getPopularShows(),
        tmdb.getPopularMovies(),
        tmdb.getTopRatedShows(),
        tmdb.getTopRatedMovies(),
        tmdb.getUpcomingMovies(),
        tmdb.getRecentlyReleasedMovies(),
        tmdb.getPopularShows(2),
        tmdb.getPopularMovies(2),
        tmdb.getTopRatedShows(2),
        tmdb.getTopRatedMovies(2),
      ]);

      const trendingItems = trendingData.results.slice(0, 21).map((item: any) => ({ ...item, media_type: 'tv' }));
      setTrending(trendingItems);
      setPopularShows([...showsData.results, ...showsData2.results]);
      setPopularMovies([...moviesData.results, ...moviesData2.results]);
      setTopRatedShows([...topRatedShowsData.results, ...topRatedShowsData2.results]);
      setTopRatedMovies([...topRatedMoviesData.results, ...topRatedMoviesData2.results]);

      // Upcoming movies now only contain future releases from the API
      setUpcoming(upcomingData.results.slice(0, 20));

      setRecentlyReleased(recentlyReleasedData.results.slice(0, 20));

      // Only set featured content from trending if no seasonal content exists
      if (!hasSeasonalHero && trendingItems.length > 0) {
        const itemsWithBackdrops = trendingItems.filter(item => item.backdrop_path && item.backdrop_path.trim() !== '');
        if (itemsWithBackdrops.length > 0) {
          setFeaturedContent(itemsWithBackdrops[0]);
        }
      }
    } catch (error) {
    } finally {
      setLoading(false);
    }
  };

  const fetchSeasonalContent = async () => {
    try {
      // Check if there's an active seasonal period
      const activePeriod = await getActiveSeasonalPeriod();

      if (activePeriod) {
        setSeasonalPeriod(activePeriod);

        let seasonalItems: any[] = [];

        // Use custom movie list if available, otherwise fall back to keyword discovery
        if (activePeriod.movies && activePeriod.movies.length > 0) {
          // Use the custom movie list from admin panel
          seasonalItems = activePeriod.movies.map((movie) => ({
            ...movie,
            media_type: 'movie'
          }));
        } else if (activePeriod.keywordId) {
          // Fallback: Fetch movies with the seasonal keyword (deprecated)
          const seasonalData = await tmdb.discoverByKeyword(activePeriod.keywordId, 1);
          seasonalItems = seasonalData.results.slice(0, 20).map((item: any) => ({
            ...item,
            media_type: 'movie'
          }));
        }

        // Randomize movie order using daily seed for variety
        const themeSeed = activePeriod.movieSeed || 0;
        const dailySeed = getDailySeed(themeSeed);
        seasonalItems = shuffleWithSeed(seasonalItems, dailySeed);

        setSeasonalMovies(seasonalItems);

        // Set hero to rotate daily from seasonal movies
        if (seasonalItems.length > 0) {
          const itemsWithBackdrops = seasonalItems.filter(item => item.backdrop_path && item.backdrop_path.trim() !== '');
          if (itemsWithBackdrops.length > 0) {
            // Use day of year to rotate through seasonal movies
            const now = new Date();
            const start = new Date(now.getFullYear(), 0, 0);
            const diff = now.getTime() - start.getTime();
            const oneDay = 1000 * 60 * 60 * 24;
            const dayOfYear = Math.floor(diff / oneDay);

            // Pick different movie each day based on day of year
            const heroIndex = dayOfYear % itemsWithBackdrops.length;
            setFeaturedContent(itemsWithBackdrops[heroIndex]);
          }
        }
        return true; // Return true if seasonal content was set
      } else {
        setSeasonalPeriod(null);
        setSeasonalMovies([]);
        return false; // Return false if no seasonal content
      }
    } catch (error) {
      setSeasonalPeriod(null);
      setSeasonalMovies([]);
      return false;
    }
  };

  const loadMoreContent = useCallback(async () => {
    if (loadingMore || !hasMore) return;

    setLoadingMore(true);
    try {
      const nextPage = page + 1;
      const [moreShows, moreMovies] = await Promise.all([
        tmdb.getPopularShows(nextPage),
        tmdb.getPopularMovies(nextPage)
      ]);

      if (moreShows.results.length === 0 && moreMovies.results.length === 0) {
        setHasMore(false);
      } else {
        // Deduplicate before adding new items
        setPopularShows(prev => {
          const combined = [...prev, ...moreShows.results];
          return combined.filter((item, index, self) =>
            index === self.findIndex(t => t.id === item.id)
          );
        });
        setPopularMovies(prev => {
          const combined = [...prev, ...moreMovies.results];
          return combined.filter((item, index, self) =>
            index === self.findIndex(t => t.id === item.id)
          );
        });
        setPage(nextPage);
      }
    } catch (error) {
    } finally {
      setLoadingMore(false);
    }
  }, [loadingMore, hasMore, page]);

  useEffect(() => {
    const handleScroll = () => {
      // Only trigger infinite scroll when actively searching
      if (searchQuery.length >= 2) {
        if (
          window.innerHeight + document.documentElement.scrollTop
          >= document.documentElement.offsetHeight - 1000
        ) {
          loadMoreContent();
        }
      }
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, [loadMoreContent, searchQuery]);

  const handleSearch = async () => {
    if (searchQuery.length < 2) {
      setSearchResults([]);
      return;
    }

    setSearchLoading(true);
    try {
      const data = await tmdb.searchMulti(searchQuery);
      const filtered = data.results.filter((item: any) =>
        item.media_type === 'tv' || item.media_type === 'movie'
      );
      setSearchResults(filtered);
    } catch (error) {
    } finally {
      setSearchLoading(false);
    }
  };

  useEffect(() => {
    const delaySearch = setTimeout(() => {
      handleSearch();
    }, 500);

    return () => clearTimeout(delaySearch);
  }, [searchQuery]);


  const scrollPositions = useRef<Record<string, number>>({});

  const MediaSection = memo(({
    title,
    items,
    icon: Icon,
    type,
    horizontal = false,
    sectionId,
    category
  }: {
    title: string;
    items: MediaItem[];
    icon: any;
    type?: string;
    horizontal?: boolean;
    sectionId?: string;
    category?: string;
  }) => {
    const scrollRef = useRef<HTMLDivElement>(null);
    const sectionKey = sectionId || title.replace(/\s+/g, '-').toLowerCase();

    // Restore scroll position after render
    useEffect(() => {
      if (scrollRef.current && scrollPositions.current[sectionKey] !== undefined) {
        scrollRef.current.scrollLeft = scrollPositions.current[sectionKey];
      }
    });

    // Save scroll position when scrolling
    const handleScroll = (e: React.UIEvent<HTMLDivElement>) => {
      scrollPositions.current[sectionKey] = e.currentTarget.scrollLeft;
    };

    // Filter out items without poster images for a professional look
    const itemsWithPosters = items.filter(item => item.poster_path && item.poster_path.trim() !== '');

    // Don't render section if no items have posters
    if (itemsWithPosters.length === 0) return null;

    return (
      <div className="space-y-4">
        <div className="flex items-center justify-between gap-3">
          <h2 className="flex items-center gap-2 text-xl md:text-2xl font-semibold tracking-tight text-white">
            {Icon && <Icon className="h-5 w-5 text-red-500 flex-shrink-0" />}
            {title}
          </h2>
          {horizontal && category && (
            <button
              className="flex items-center gap-1 text-sm text-gray-400 hover:text-white transition-colors whitespace-nowrap"
              onClick={() => router.push(`/browse/${category}`)}
            >
              See all
              <ChevronRight className="h-4 w-4" />
            </button>
          )}
        </div>
        {horizontal ? (
          <div className="overflow-x-auto -mx-2 px-2 -my-3 py-3" ref={scrollRef} onScroll={handleScroll}>
            <div className="flex gap-4 md:gap-6 pb-3" style={{ width: 'max-content' }}>
              {itemsWithPosters.slice(0, 20).map((item, index) => (
                <ReusableMediaCard
                  key={`${sectionId || title.replace(/\s+/g, '-').toLowerCase()}-${item.id}-${type || item.media_type || 'unknown'}-${index}`}
                  item={item}
                  mediaType={type}
                  showAddButton={true}
                  size="medium"
                />
              ))}
            </div>
          </div>
        ) : (
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 gap-3 md:gap-4">
            {itemsWithPosters.map((item, index) => (
              <ReusableMediaCard key={`${sectionId || title.replace(/\s+/g, '-').toLowerCase()}-${item.id}-${type || item.media_type || 'unknown'}-${index}`} item={item} mediaType={type} showAddButton={true} size="small" />
            ))}
          </div>
        )}
      </div>
    );
  });

  const HeroSection = ({ item }: { item: MediaItem }) => {
    const mediaType = item.media_type || (item.first_air_date ? 'tv' : 'movie');
    const title = item.title || item.name;
    const year = (item.release_date || item.first_air_date) ? new Date(item.release_date || item.first_air_date!).getFullYear() : null;
    const badgeLabel = seasonalPeriod
      ? `${seasonalPeriod.name} Featured`
      : (mediaType === 'tv' ? 'Trending TV Series' : 'Trending Movie');

    return (
      <CinematicHero
        backdropPath={item.backdrop_path}
        title={title || ''}
        overview={item.overview}
        rating={item.vote_average}
        year={year}
        badgeLabel={badgeLabel}
        href={`/${mediaType}/${item.id}`}
      />
    );
  };

  return (
    <div className="min-h-screen bg-black">
      {/* Header with integrated search */}
      <Header showSearch={true} transparent={true} page="landing" />

      {/* Hero Section */}
      {!searchQuery && !loading && featuredContent && (
        <HeroSection item={featuredContent} />
      )}

      {/* Main Content — pulled up to overlap the hero fade for cinematic depth */}
      <main className={!searchQuery && !loading && featuredContent ? "relative z-10 -mt-16 md:-mt-24" : "pt-32 md:pt-24"}>
        {/* Search Results */}
        {searchQuery && (
          <div className="container mx-auto px-4 py-8">
            {searchLoading ? (
              <div className="space-y-4">
                <Skeleton className="h-8 w-64" />
                <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-5 lg:grid-cols-6 xl:grid-cols-7 2xl:grid-cols-8 gap-3 md:gap-4">
                  {Array.from({ length: 8 }).map((_, i) => (
                    <Skeleton key={i} className="aspect-[2/3] rounded-xl" />
                  ))}
                </div>
              </div>
            ) : searchResults.length > 0 ? (
              <div className="space-y-6">
                <div className="flex items-center gap-3">
                  <Search className="h-6 w-6 text-red-500" />
                  <h2 className="text-2xl font-bold text-white">Search Results for "{searchQuery}"</h2>
                </div>
                <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 gap-3 md:gap-4">
                  {searchResults.filter(item => item.poster_path).map((item, index) => (
                    <ReusableMediaCard
                      key={`search-${item.id}-${item.media_type}-${index}`}
                      item={item}
                      mediaType={item.media_type as 'tv' | 'movie'}
                      showAddButton={true}
                      size="small"
                    />
                  ))}
                </div>
              </div>
            ) : (
              <div className="text-center py-12">
                <div className="w-24 h-24 mx-auto mb-4 rounded-full bg-gray-800 flex items-center justify-center">
                  <Search className="h-10 w-10 text-gray-600" />
                </div>
                <p className="text-gray-400 text-lg">No results found for "{searchQuery}"</p>
                <p className="text-gray-500 text-sm mt-2">Try adjusting your search terms</p>
              </div>
            )}
          </div>
        )}

        {/* Content Tabs */}
        {!searchQuery && (
          <div className="bg-gradient-to-b from-transparent to-black py-8 md:py-12">
            <Tabs defaultValue="all" className="space-y-8 border-none outline-none">
              <div className="container mx-auto px-4">
                <div className="flex justify-center">
                  <div className="overflow-x-auto scrollbar-hide w-full">
                    <div className="flex justify-center min-w-max">
                      <TabsList className="bg-gray-900/80 border-2 border-gray-700/50 backdrop-blur-sm rounded-xl p-1 md:p-2 shadow-2xl" style={{ touchAction: 'manipulation' }}>
                        <TabsTrigger
                          value="all"
                          className="data-[state=active]:bg-red-600 data-[state=active]:text-white px-3 md:px-6 py-2 md:py-3 text-sm md:text-base font-semibold rounded-lg transition-all duration-300 hover:bg-white/10 whitespace-nowrap"
                        >
                          <Sparkles className="h-4 w-4 md:h-5 md:w-5 mr-1 md:mr-2" />
                          <span className="hidden sm:inline">All Content</span>
                          <span className="sm:hidden">All</span>
                        </TabsTrigger>
                        <TabsTrigger
                          value="trending"
                          className="data-[state=active]:bg-red-600 data-[state=active]:text-white px-3 md:px-6 py-2 md:py-3 text-sm md:text-base font-semibold rounded-lg transition-all duration-300 hover:bg-white/10 whitespace-nowrap"
                        >
                          <TrendingUp className="h-4 w-4 md:h-5 md:w-5 mr-1 md:mr-2" />
                          Trending
                        </TabsTrigger>
                        <TabsTrigger
                          value="shows"
                          className="data-[state=active]:bg-red-600 data-[state=active]:text-white px-3 md:px-6 py-2 md:py-3 text-sm md:text-base font-semibold rounded-lg transition-all duration-300 hover:bg-white/10 whitespace-nowrap"
                        >
                          <Tv className="h-4 w-4 md:h-5 md:w-5 mr-1 md:mr-2" />
                          <span className="hidden sm:inline">TV Shows</span>
                          <span className="sm:hidden">Shows</span>
                        </TabsTrigger>
                        <TabsTrigger
                          value="movies"
                          className="data-[state=active]:bg-red-600 data-[state=active]:text-white px-3 md:px-6 py-2 md:py-3 text-sm md:text-base font-semibold rounded-lg transition-all duration-300 hover:bg-white/10 whitespace-nowrap"
                        >
                          <Film className="h-4 w-4 md:h-5 md:w-5 mr-1 md:mr-2" />
                          Movies
                        </TabsTrigger>
                      </TabsList>
                    </div>
                  </div>
                </div>
              </div>

            <TabsContent value="all" className="space-y-12 container mx-auto px-4 border-none outline-none">
              {loading ? (
                <div className="space-y-8">
                  {Array.from({ length: 4 }).map((_, sectionIndex) => (
                    <div key={sectionIndex} className="space-y-4">
                      <Skeleton className="h-8 w-64" />
                      <div className="overflow-x-auto scrollbar-hide">
                        <div className="flex gap-6 pb-6" style={{ width: 'max-content' }}>
                          {Array.from({ length: 8 }).map((_, i) => (
                            <Skeleton key={i} className="w-48 aspect-[2/3] rounded-xl flex-shrink-0" />
                          ))}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <>
                  {/* Render sections in configured order */}
                  {sectionConfig.map((section) => {
                    if (!section.enabled) return null;
                    if (section.requiresAuth && !user) return null;

                    switch (section.id) {
                      case 'category-browser':
                        return <CategoryBrowser key={section.id} />;

                      case 'seasonal':
                        return seasonalPeriod && seasonalMovies.length > 0 ? (
                          <MediaSection
                            key={section.id}
                            title={`${seasonalPeriod.name} Movies`}
                            items={seasonalMovies}
                            icon={Calendar}
                            type="movie"
                            horizontal={true}
                            sectionId={`seasonal-${seasonalPeriod.id}`}
                            category={`seasonal/${seasonalPeriod.id}`}
                          />
                        ) : null;

                      case 'recommended-for-you':
                        return (
                          <PersonalizedRow
                            key={section.id}
                            title="Recommended For You"
                            category="personalized"
                            description="Based on your watchlist and viewing preferences"
                            className="mb-8"
                          />
                        );

                      case 'language-specific':
                        return userLanguages.length > 1 ? (
                          <PersonalizedRow
                            key={section.id}
                            title={`Popular in ${userLanguages[0] === 'en' ? 'English' : userLanguages[0].toUpperCase()}`}
                            category="language_specific"
                            language={userLanguages[0]}
                            description="Content in your preferred language"
                            className="mb-8"
                          />
                        ) : null;

                      case 'continue-watching':
                        return (
                          <PersonalizedRow
                            key={section.id}
                            title="Continue Watching"
                            category="continue"
                            description="Pick up where you left off"
                            className="mb-8"
                          />
                        );

                      case 'watch-again':
                        return (
                          <PersonalizedRow
                            key={section.id}
                            title="Watch Again"
                            category="watch_again"
                            description="Revisit your favorites"
                            className="mb-8"
                          />
                        );

                      case 'trending-personalized':
                        return (
                          <PersonalizedRow
                            key={section.id}
                            title="Trending Now"
                            category="trending"
                            description="What everyone's watching today"
                            className="mb-8"
                          />
                        );

                      case 'dynamic-personalized':
                        return <DynamicPersonalizedRows key={section.id} />;

                      case 'trending':
                        // Only show for non-logged-in users (logged-in users get trending-personalized)
                        return !user ? (
                          <MediaSection
                            key={section.id}
                            title="Trending Now"
                            items={dedupedRows.all.trending.slice(1)}
                            icon={TrendingUp}
                            horizontal={true}
                            sectionId="all-trending"
                            category="trending"
                          />
                        ) : null;

                      case 'popular-tv':
                        return (
                          <MediaSection
                            key={section.id}
                            title="Popular TV Shows"
                            items={dedupedRows.all.popularShows}
                            icon={Tv}
                            type="tv"
                            horizontal={true}
                            sectionId="all-popular-tv"
                            category="popular-shows"
                          />
                        );

                      case 'popular-movies':
                        return (
                          <MediaSection
                            key={section.id}
                            title="Popular Movies"
                            items={dedupedRows.all.popularMovies}
                            icon={Film}
                            type="movie"
                            horizontal={true}
                            sectionId="all-popular-movies"
                            category="popular-movies"
                          />
                        );

                      case 'top-rated-tv':
                        return (
                          <MediaSection
                            key={section.id}
                            title="All-Time Top Rated TV Shows"
                            items={dedupedRows.all.topRatedShows}
                            icon={Star}
                            type="tv"
                            horizontal={true}
                            sectionId="all-top-rated-tv"
                            category="top-rated-shows"
                          />
                        );

                      case 'top-rated-movies':
                        return (
                          <MediaSection
                            key={section.id}
                            title="All-Time Top Rated Movies"
                            items={dedupedRows.all.topRatedMovies}
                            icon={Star}
                            type="movie"
                            horizontal={true}
                            sectionId="all-top-rated-movies"
                            category="top-rated-movies"
                          />
                        );

                      case 'recently-released':
                        return (
                          <MediaSection
                            key={section.id}
                            title="Recently Released"
                            items={dedupedRows.all.recentlyReleased}
                            icon={Clock}
                            type="movie"
                            horizontal={true}
                            sectionId="recently-released-movies"
                            category="recently-released"
                          />
                        );

                      case 'coming-soon':
                        return (
                          <MediaSection
                            key={section.id}
                            title="Coming Soon"
                            items={dedupedRows.all.upcoming}
                            icon={Calendar}
                            type="movie"
                            horizontal={true}
                            sectionId="all-upcoming-movies"
                            category="upcoming"
                          />
                        );

                      default:
                        return null;
                    }
                  })}

                  {loadingMore && searchQuery.length >= 2 && (
                    <div className="flex justify-center py-8">
                      <div className="flex items-center gap-2 text-gray-400">
                        <div className="animate-spin rounded-full h-6 w-6 border-2 border-red-500 border-t-transparent"></div>
                        <span>Loading more content...</span>
                      </div>
                    </div>
                  )}
                </>
              )}
            </TabsContent>

            <TabsContent value="trending" className="space-y-12 container mx-auto px-4 border-none outline-none">
              <MediaSection
                title="Trending Now"
                items={dedupedRows.trendingTab.trending}
                icon={TrendingUp}
                horizontal={true}
                sectionId="trending-tab"
                category="trending"
              />
              <MediaSection
                title="Popular Movies"
                items={dedupedRows.trendingTab.popularMovies}
                icon={Film}
                type="movie"
                horizontal={true}
                sectionId="trending-popular-movies"
                category="popular"
              />
              <MediaSection
                title="Popular TV Shows"
                items={dedupedRows.trendingTab.popularShows}
                icon={Tv}
                type="tv"
                horizontal={true}
                sectionId="trending-popular-shows"
                category="popular"
              />
              <MediaSection
                title="Recently Released"
                items={dedupedRows.trendingTab.recentlyReleased}
                icon={Calendar}
                type="movie"
                horizontal={true}
                sectionId="trending-recently-released"
                category="popular"
              />
            </TabsContent>

            <TabsContent value="shows" className="space-y-12 container mx-auto px-4 border-none outline-none">
              <MediaSection
                title="Popular TV Shows"
                items={dedupedRows.showsTab.popularShows}
                icon={Tv}
                type="tv"
                horizontal={true}
                sectionId="shows-tab"
                category="popular"
              />
              <MediaSection
                title="All-Time Top Rated TV Shows"
                items={dedupedRows.showsTab.topRatedShows}
                icon={Star}
                type="tv"
                horizontal={true}
                sectionId="shows-top-rated"
                category="top-rated"
              />
              <MediaSection
                title="Trending TV"
                items={dedupedRows.showsTab.trending}
                icon={TrendingUp}
                type="tv"
                horizontal={true}
                sectionId="shows-trending"
                category="trending"
              />
            </TabsContent>

            <TabsContent value="movies" className="space-y-12 container mx-auto px-4 border-none outline-none">
              <MediaSection
                title="Popular Movies"
                items={dedupedRows.moviesTab.popularMovies}
                icon={Film}
                type="movie"
                horizontal={true}
                sectionId="movies-tab"
                category="popular"
              />
              <MediaSection
                title="All-Time Top Rated Movies"
                items={dedupedRows.moviesTab.topRatedMovies}
                icon={Star}
                type="movie"
                horizontal={true}
                sectionId="movies-top-rated"
                category="top-rated"
              />
              <MediaSection
                title="Recently Released"
                items={dedupedRows.moviesTab.recentlyReleased}
                icon={Calendar}
                type="movie"
                horizontal={true}
                sectionId="movies-recently-released"
                category="popular"
              />
              <MediaSection
                title="Coming Soon"
                items={dedupedRows.moviesTab.upcoming}
                icon={Clock}
                type="movie"
                horizontal={true}
                sectionId="movies-coming-soon"
                category="popular"
              />
            </TabsContent>
            </Tabs>
          </div>
        )}
      </main>

    </div>
  );
}